import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { amountSchema, fractionSchema, type TradeInCase, type VehicleDetail } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Button } from '../../../components/ui/Button';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Sheet } from '../../../components/ui/Sheet';
import { Textarea } from '../../../components/ui/Textarea';
import { fitsSchema, serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { toOptionalNumber, toOptionalText } from '../../estimates/estimates-display';

export interface TradeInSheetProps {
  isOpen: boolean;
  vehicle: VehicleDetail;
  tradeIn: TradeInCase | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

interface TradeInForm {
  newVehicleListCode: string;
  newVehicleModel: string;
  newVehicleProfit: string;
  newVehicleMarginPercent: string;
  profitSource: string;
  profitSourceDate: string;
  demandLevel: string;
  stockLevel: string;
  supportCap: string;
  fundingDepartment: string;
  lossRecoveryAmount: string;
  lossRecoveryNote: string;
}

const formFrom = (tradeIn: TradeInCase | null): TradeInForm => ({
  newVehicleListCode: tradeIn?.newVehicleListCode ?? '',
  newVehicleModel: tradeIn?.newVehicleModel ?? '',
  newVehicleProfit: tradeIn?.newVehicleProfit === null || tradeIn === null ? '' : String(tradeIn.newVehicleProfit),
  // Percentages are fractions on the wire; only the UI ever prints a `%` (plan §11.6).
  newVehicleMarginPercent: tradeIn?.newVehicleMargin === null || tradeIn === null ? '' : String(Math.round(tradeIn.newVehicleMargin * 10_000) / 100),
  profitSource: tradeIn?.profitSource ?? '',
  profitSourceDate: tradeIn?.profitSourceDate ?? '',
  demandLevel: tradeIn?.demandLevel ?? '',
  stockLevel: tradeIn?.stockLevel ?? '',
  supportCap: tradeIn?.supportCap === null || tradeIn === null ? '' : String(tradeIn.supportCap),
  fundingDepartment: tradeIn?.fundingDepartment ?? '',
  lossRecoveryAmount: tradeIn?.lossRecoveryAmount === null || tradeIn === null ? '' : String(tradeIn.lossRecoveryAmount),
  lossRecoveryNote: tradeIn?.lossRecoveryNote ?? '',
});

/**
 * The trade-in case (plan §5.9, PP §7.4).
 *
 * Every figure here is a **scenario** figure. They produce `supportRate` and `support` beside the
 * ceiling — "maximum trade-in offer = ceiling + support" — and never enter `total`, `retail` or the
 * dashboard, because Finance has not defined the funding and allocation policy that would make them
 * binding. The sheet says so out loud rather than leaving somebody to find out later.
 */
export const TradeInSheet = ({ isOpen, vehicle, tradeIn, onClose, onSaved }: TradeInSheetProps) => {
  const { t } = useI18nTranslation();
  const [form, setForm] = useState<TradeInForm>(() => formFrom(tradeIn));
  const [error, setError] = useState<string | null>(null);
  const upsert = useMutation(orpc.tradeIn.upsert.mutationOptions());

  useEffect(() => {
    if (isOpen) {
      setForm(formFrom(tradeIn));
      setError(null);
    }
  }, [isOpen, tradeIn]);

  const change = (patch: Partial<TradeInForm>) => setForm((current) => ({ ...current, ...patch }));

  const marginPercent = toOptionalNumber(form.newVehicleMarginPercent);
  /** Percentages are fractions on the wire (plan §11.6), and the contract's fraction is 0–1. */
  const margin = marginPercent === null ? null : Math.round(marginPercent * 100) / 10_000;

  const amounts = {
    newVehicleProfit: toOptionalNumber(form.newVehicleProfit),
    supportCap: toOptionalNumber(form.supportCap),
    lossRecoveryAmount: toOptionalNumber(form.lossRecoveryAmount),
  };

  /**
   * What the contract would refuse, checked before the sheet asks for it rather than after (plan §2.5).
   * `max={100}` and `min={0}` on a number input are spinner hints and stop nobody typing: `150` in the
   * `%` box became a fraction of `1.5`, and a typed `-5` went out as a negative amount.
   */
  const marginOutOfRange = !fitsSchema(fractionSchema, margin);
  const outOfRange = (Object.keys(amounts) as (keyof typeof amounts)[]).filter((key) => !fitsSchema(amountSchema, amounts[key]));
  const canSave = !marginOutOfRange && outOfRange.length === 0;

  const save = async () => {
    setError(null);

    if (!canSave) {
      return;
    }

    try {
      await upsert.mutateAsync({
        vehicleId: vehicle.id,
        expectedRevision: vehicle.revision,
        newVehicleListCode: toOptionalText(form.newVehicleListCode),
        newVehicleModel: toOptionalText(form.newVehicleModel),
        newVehicleProfit: amounts.newVehicleProfit,
        newVehicleMargin: margin,
        profitSource: toOptionalText(form.profitSource),
        profitSourceDate: toOptionalText(form.profitSourceDate),
        demandLevel: (toOptionalText(form.demandLevel) as TradeInCase['demandLevel']) ?? null,
        stockLevel: (toOptionalText(form.stockLevel) as TradeInCase['stockLevel']) ?? null,
        supportCap: amounts.supportCap,
        fundingDepartment: toOptionalText(form.fundingDepartment),
        lossRecoveryAmount: amounts.lossRecoveryAmount,
        lossRecoveryNote: toOptionalText(form.lossRecoveryNote),
      });

      await onSaved();
      onClose();
    } catch (caught: unknown) {
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('vehicles.tradeInTitle')}
      description={t('vehicles.tradeInBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={upsert.isPending}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {t('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <p className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-sm text-[#a16207]">{t('vehicles.tradeInScenarioOnly')}</p>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            id="trade-in-code"
            label={t('vehicles.newVehicleListCode')}
          >
            <Input
              id="trade-in-code"
              value={form.newVehicleListCode}
              onChange={(event) => change({ newVehicleListCode: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-model"
            label={t('vehicles.newVehicleModel')}
          >
            <Input
              id="trade-in-model"
              value={form.newVehicleModel}
              onChange={(event) => change({ newVehicleModel: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-profit"
            label={t('vehicles.newVehicleProfit')}
            error={outOfRange.includes('newVehicleProfit') ? t('estimates.numberOutOfRange') : undefined}
          >
            <Input
              id="trade-in-profit"
              type="number"
              min={0}
              step={0.001}
              value={form.newVehicleProfit}
              onChange={(event) => change({ newVehicleProfit: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-margin"
            label={t('vehicles.newVehicleMargin')}
            error={marginOutOfRange ? t('vehicles.marginOutOfRange') : undefined}
          >
            <Input
              id="trade-in-margin"
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={form.newVehicleMarginPercent}
              onChange={(event) => change({ newVehicleMarginPercent: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-source"
            label={t('vehicles.profitSource')}
          >
            <Input
              id="trade-in-source"
              value={form.profitSource}
              onChange={(event) => change({ profitSource: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-source-date"
            label={t('vehicles.profitSourceDate')}
          >
            <Input
              id="trade-in-source-date"
              type="date"
              dir="ltr"
              value={form.profitSourceDate}
              onChange={(event) => change({ profitSourceDate: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-demand"
            label={t('vehicles.demandLevel')}
          >
            <Select
              id="trade-in-demand"
              value={form.demandLevel}
              placeholder={t('common.notSet')}
              onChange={(event) => change({ demandLevel: event.target.value })}
              options={['HIGH', 'MEDIUM', 'LOW'].map((level) => ({ value: level, label: t(`enums.demandLevel.${level}`) }))}
            />
          </FormField>
          <FormField
            id="trade-in-stock"
            label={t('vehicles.stockLevel')}
          >
            <Select
              id="trade-in-stock"
              value={form.stockLevel}
              placeholder={t('common.notSet')}
              onChange={(event) => change({ stockLevel: event.target.value })}
              options={['LOW', 'MEDIUM', 'HIGH'].map((level) => ({ value: level, label: t(`enums.stockLevel.${level}`) }))}
            />
          </FormField>
          <FormField
            id="trade-in-cap"
            label={t('vehicles.supportCap')}
            error={outOfRange.includes('supportCap') ? t('estimates.numberOutOfRange') : undefined}
          >
            <Input
              id="trade-in-cap"
              type="number"
              min={0}
              step={0.001}
              value={form.supportCap}
              onChange={(event) => change({ supportCap: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-department"
            label={t('vehicles.fundingDepartment')}
          >
            <Input
              id="trade-in-department"
              value={form.fundingDepartment}
              onChange={(event) => change({ fundingDepartment: event.target.value })}
            />
          </FormField>
          <FormField
            id="trade-in-loss"
            label={t('vehicles.lossRecoveryAmount')}
            error={outOfRange.includes('lossRecoveryAmount') ? t('estimates.numberOutOfRange') : undefined}
          >
            <Input
              id="trade-in-loss"
              type="number"
              min={0}
              step={0.001}
              value={form.lossRecoveryAmount}
              onChange={(event) => change({ lossRecoveryAmount: event.target.value })}
            />
          </FormField>
        </div>

        <FormField
          id="trade-in-loss-note"
          label={t('vehicles.lossRecoveryNote')}
        >
          <Textarea
            id="trade-in-loss-note"
            rows={3}
            value={form.lossRecoveryNote}
            onChange={(event) => change({ lossRecoveryNote: event.target.value })}
          />
        </FormField>
      </div>
    </Sheet>
  );
};
